/**
 * ErrorState — "Something went wrong", a human sentence, and a RETRY THAT ACTUALLY RETRIES.
 *
 * WHY IT IS A COMPONENT AND NOT A PATTERN. The prototype draws no error state anywhere: `skeleton`,
 * `spinner`, `aria-busy` and `Something went wrong` all return ZERO across the whole 636 KB file,
 * re-verified by script. So every error state in both apps is new work, and if it is new work in
 * fourteen screens it will be fourteen different apologies with three working retry buttons
 * (docs/ui/03-components.md).
 *
 * THREE RULES BAKED IN RATHER THAN LEFT TO A REVIEW CHECKLIST:
 *
 *   1. `role="alert"`. A failure that appears silently for a screen-reader user is a failure they
 *      never learn about. Mantine's `Alert` does NOT set a role by itself — verified in its source
 *      — so it is set here.
 *   2. NEVER SURFACE A RAW ERROR CODE OR A STACK TRACE. The API's `detail` field is explicitly
 *      "for a human reading a log", and on the author-facing surfaces it may name only the
 *      caller's own row. `technicalDetail` is rendered only when the caller opts in, and is meant
 *      for an operator console rather than for an author.
 *   3. THE RETRY IS WIRED. The brand repo's version has no retry at all; ours takes `onRetry` and
 *      shows a busy state while the retry runs, because a button that does nothing visible gets
 *      pressed four more times.
 */
import { Alert, Button, Group, Stack, Text } from '@mantine/core';
import type { ReactNode } from 'react';
import { useUiText } from '../../texts/ui-texts';

export interface ErrorStateProps {
  /** Short, human, and never a code. Defaults to the plainest thing that is always true. */
  title?: string;
  /** One or two sentences saying what the person can do. */
  children?: ReactNode;
  /** Wire it and the button appears; omit it and there is no button pretending to help. */
  onRetry?: () => void;
  /** Shows the retry button as busy. The caller owns the flag; this component owns the affordance. */
  retrying?: boolean;
  retryLabel?: string;
  /** A second, non-destructive way out — "Sign in instead", "Go back". */
  secondaryAction?: ReactNode;
  /**
   * Operator-facing detail. Rendered in small dimmed text when supplied. NEVER pass an exception
   * message or a stack trace from a browser build: it reaches the author's screen.
   */
  technicalDetail?: string;
}

export function ErrorState({
  title,
  children,
  onRetry,
  retrying = false,
  retryLabel,
  secondaryAction,
  technicalDetail,
}: ErrorStateProps) {
  const t = useUiText();
  return (
    <Alert
      role="alert"
      variant="light"
      color="red"
      title={title ?? t('errorState.title')}
      // `Alert` has no live-region semantics of its own; the role above supplies them. The colour
      // is a Mantine key, never a raw value — the token layer decides what "red" is.
    >
      <Stack gap="sm">
        {children === undefined ? null : <div>{children}</div>}
        {technicalDetail === undefined ? null : (
          <Text size="xs" c="dimmed">
            {technicalDetail}
          </Text>
        )}
        {onRetry === undefined && secondaryAction === undefined ? null : (
          <Group gap="sm">
            {onRetry === undefined ? null : (
              <Button variant="filled" size="sm" onClick={onRetry} loading={retrying}>
                {retryLabel ?? t('errorState.retry')}
              </Button>
            )}
            {secondaryAction}
          </Group>
        )}
      </Stack>
    </Alert>
  );
}
